import { z } from 'zod';

export const HhMmSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

export const WeekdayDropOffSchema = z.discriminatedUnion('open', [
  z.object({
    open: z.literal(true),
    dropOffTime: HhMmSchema,
    maxVehicles: z.number().int().min(1).max(999),
  }),
  z.object({
    open: z.literal(false),
    dropOffTime: z.null(),
    maxVehicles: z.literal(0),
  }),
]);

export type WeekdayDropOff = z.infer<typeof WeekdayDropOffSchema>;

export const WEEKDAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

export type WeekdayKey = (typeof WEEKDAY_KEYS)[number];

export const WeekdayDropOffsSchema = z.object({
  monday: WeekdayDropOffSchema,
  tuesday: WeekdayDropOffSchema,
  wednesday: WeekdayDropOffSchema,
  thursday: WeekdayDropOffSchema,
  friday: WeekdayDropOffSchema,
  saturday: WeekdayDropOffSchema,
  sunday: WeekdayDropOffSchema,
});

export type WeekdayDropOffs = z.infer<typeof WeekdayDropOffsSchema>;

function openDay(dropOffTime = '08:00', maxVehicles = 8): WeekdayDropOff {
  return { open: true, dropOffTime, maxVehicles };
}

function closedDay(): WeekdayDropOff {
  return { open: false, dropOffTime: null, maxVehicles: 0 };
}

export const DEFAULT_WEEKDAY_DROP_OFFS: WeekdayDropOffs = {
  monday: openDay(),
  tuesday: openDay(),
  wednesday: openDay(),
  thursday: openDay(),
  friday: openDay(),
  saturday: closedDay(),
  sunday: closedDay(),
};

export const PlaceScheduleSettingsBodySchema = z.object({
  weekdays: WeekdayDropOffsSchema,
  autoConfirmAppointments: z.boolean().default(false),
});

export type PlaceScheduleSettingsBody = z.infer<typeof PlaceScheduleSettingsBodySchema>;

export const PlaceScheduleSettingsResponseSchema = z.object({
  placeId: z.uuid(),
  weekdays: WeekdayDropOffsSchema,
  autoConfirmAppointments: z.boolean(),
  updatedAt: z.string(),
});

export type PlaceScheduleSettingsResponse = z.infer<typeof PlaceScheduleSettingsResponseSchema>;

/** GET returns null settings when never configured. */
export const GetPlaceScheduleSettingsResponseSchema = z.object({
  placeId: z.uuid(),
  settings: PlaceScheduleSettingsResponseSchema.omit({ placeId: true }).nullable(),
});

export type GetPlaceScheduleSettingsResponse = z.infer<
  typeof GetPlaceScheduleSettingsResponseSchema
>;
